import { PictureOutlined, SaveOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Empty, Form, Input, Select, Skeleton, Typography, Upload, message } from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { useCallback, useEffect, useState } from 'react'
import { createSiteEntry, listSiteEntries, siteAssetVariantUrl, updateSiteEntry, uploadSiteAsset } from '../../api'
import type { SaasApiError } from '../../api'
import type { ContentEntry } from '../../types'

const { Title, Text, Paragraph } = Typography

const CONTENT_TYPE_PAGE = 'page'
const HOME_SLUG = 'home'

function heroTextFromBlocks(blocks: Array<Record<string, unknown>>): string {
  const hero = blocks.find((b) => b.type === 'rich_text')
  const data = hero?.data as { text?: string } | undefined
  return data?.text ?? ''
}

function heroImageAssetIdFromBlocks(blocks: Array<Record<string, unknown>>): string | null {
  const hero = blocks.find((b) => b.type === 'hero') as { asset_refs?: Array<{ asset_id?: string }> } | undefined
  return hero?.asset_refs?.[0]?.asset_id ?? null
}

// Legacy "首页" supports Featured Items + Featured Exhibitions + Featured
// Research blocks (spec §13 — `get_home` in `public_site.py` already
// reads all three `entry_grid` blocks by their `id` substring; this admin
// UI previously only exposed the Items one). One `entry_grid` block per
// featured collection, keyed by a stable `id` so the public renderer can
// tell them apart.
function idsFromGridBlock(blocks: Array<Record<string, unknown>>, blockId: string): string[] {
  const grid = blocks.find((b) => b.type === 'entry_grid' && b.id === blockId)
  return (grid?.entry_refs as string[] | undefined) ?? (grid?.resolved_entry_ids as string[] | undefined) ?? []
}

/**
 * LiangCollection Full Website Absorption — "首页 / 入口页" (Homepage +
 * Entry Pages) editor. Backed by the generic `page` content type +
 * `blocks_json` (spec §"Pages / Homepage" — not a hardcoded homepage
 * table): editing this "home" page entry directly changes what
 * `public_site.get_home()` renders at "/" in the Local Public Website.
 *
 * GAP this round (see docs/migration/LIANGCOLLECTION_UI_TOKEN_MAP.md):
 * Legacy's 4 separate "入口页编辑" screens (首页/展览入口/研究入口/主题收藏入口)
 * are collapsed into this single Homepage editor — 展览入口/研究入口/主题收藏入口
 * each having their own dedicated landing-page copy/hero/SEO config is
 * NOT yet split out as independent pages.
 */
export default function PagesTab({ workspaceId, siteId }: { workspaceId: string; siteId: string }) {
  const [homePage, setHomePage] = useState<ContentEntry | null | undefined>(undefined)
  const [items, setItems] = useState<ContentEntry[]>([])
  const [exhibitions, setExhibitions] = useState<ContentEntry[]>([])
  const [research, setResearch] = useState<ContentEntry[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [heroImageAssetId, setHeroImageAssetId] = useState<string | null>(null)
  const [heroFileList, setHeroFileList] = useState<UploadFile[]>([])
  const [form] = Form.useForm<{
    heroText: string
    featuredItemIds: string[]
    featuredExhibitionIds: string[]
    featuredResearchIds: string[]
  }>()

  const load = useCallback(() => {
    setError(null)
    listSiteEntries(workspaceId, siteId, CONTENT_TYPE_PAGE)
      .then((r) => {
        const home = r.data.find((e) => e.slug === HOME_SLUG) ?? null
        setHomePage(home)
        if (home) {
          form.setFieldsValue({
            heroText: heroTextFromBlocks(home.blocks),
            featuredItemIds: idsFromGridBlock(home.blocks, 'home-featured'),
            featuredExhibitionIds: idsFromGridBlock(home.blocks, 'home-featured-exhibitions'),
            featuredResearchIds: idsFromGridBlock(home.blocks, 'home-featured-research'),
          })
          setHeroImageAssetId(heroImageAssetIdFromBlocks(home.blocks))
        }
      })
      .catch((err) => setError((err as SaasApiError).message))
    listSiteEntries(workspaceId, siteId, 'item').then((r) => setItems(r.data))
    listSiteEntries(workspaceId, siteId, 'exhibition').then((r) => setExhibitions(r.data))
    listSiteEntries(workspaceId, siteId, 'research').then((r) => setResearch(r.data))
  }, [workspaceId, siteId, form])

  useEffect(load, [load])

  const buildBlocks = (
    heroText: string,
    featuredItemIds: string[],
    featuredExhibitionIds: string[],
    featuredResearchIds: string[],
    heroAssetId: string | null,
  ) => [
    {
      id: 'home-hero-image',
      type: 'hero',
      mode: heroAssetId ? 'asset_ref' : 'none',
      data: { heading: '', subheading: '', alignment: 'left', variant: 'default' },
      asset_refs: heroAssetId ? [{ asset_id: heroAssetId, role: 'original' }] : [],
    },
    { id: 'home-hero', type: 'rich_text', mode: 'inline', data: { text: heroText } },
    { id: 'home-featured', type: 'entry_grid', mode: 'entry_ref', entry_refs: featuredItemIds, resolved_entry_ids: featuredItemIds },
    {
      id: 'home-featured-exhibitions',
      type: 'entry_grid',
      mode: 'entry_ref',
      entry_refs: featuredExhibitionIds,
      resolved_entry_ids: featuredExhibitionIds,
    },
    {
      id: 'home-featured-research',
      type: 'entry_grid',
      mode: 'entry_ref',
      entry_refs: featuredResearchIds,
      resolved_entry_ids: featuredResearchIds,
    },
  ]

  const onSave = async () => {
    const values = await form.validateFields()
    setSaving(true)
    try {
      let resolvedHeroAssetId = heroImageAssetId
      const heroFile = heroFileList[0]?.originFileObj
      if (heroFile) {
        const { data: asset } = await uploadSiteAsset(workspaceId, siteId, heroFile)
        resolvedHeroAssetId = asset.id
        setHeroImageAssetId(asset.id)
        setHeroFileList([])
      }
      const blocks = buildBlocks(
        values.heroText,
        values.featuredItemIds ?? [],
        values.featuredExhibitionIds ?? [],
        values.featuredResearchIds ?? [],
        resolvedHeroAssetId,
      )
      if (homePage) {
        const updated = await updateSiteEntry(workspaceId, siteId, homePage.id, { blocks })
        setHomePage(updated.data)
      } else {
        const created = await createSiteEntry(workspaceId, siteId, {
          content_type: CONTENT_TYPE_PAGE,
          slug: HOME_SLUG,
          label: '首页',
          blocks,
          status: 'published',
        })
        setHomePage(created.data)
      }
      message.success('首页已保存')
    } catch (err) {
      message.error((err as SaasApiError).message)
    } finally {
      setSaving(false)
    }
  }

  if (error) return <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />
  if (homePage === undefined) return <Skeleton active paragraph={{ rows: 6 }} style={{ marginTop: 16 }} />

  return (
    <div style={{ marginTop: 16, maxWidth: 720 }}>
      <Title level={5}>首页</Title>
      <Paragraph type="secondary">
        编辑首页的主图、欢迎语，以及首页展示的精选条目 / 精选展览 / 精选研究。保存后将直接更新前台首页（"/"）读取的
        "home" 页面数据，无需单独的首页配置表。
      </Paragraph>
      {items.length === 0 && <Empty description="请先创建条目，才能在首页精选展示" style={{ margin: '24px 0' }} />}
      <Card>
        <Form form={form} layout="vertical">
          <Form.Item label="主图（Hero Background Image）">
            {heroImageAssetId && heroFileList.length === 0 && (
              <img
                src={siteAssetVariantUrl(workspaceId, heroImageAssetId, 'web')}
                alt="首页主图"
                style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 6, marginBottom: 8, display: 'block' }}
              />
            )}
            <Upload
              listType="picture"
              maxCount={1}
              fileList={heroFileList}
              beforeUpload={() => false}
              onChange={({ fileList: next }) => setHeroFileList(next)}
              accept="image/*"
            >
              <Button icon={<PictureOutlined />}>{heroImageAssetId ? '替换图片' : '上传图片'}</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="heroText" label="欢迎语" rules={[{ required: true, message: '请输入欢迎语' }]}>
            <Input.TextArea rows={3} placeholder="欢迎访问 LiangCollection —— 一个私人收藏的中国书画、瓷器与摄影作品集。" />
          </Form.Item>
          <Form.Item name="featuredItemIds" label="精选条目">
            <Select mode="multiple" placeholder="选择在首页展示的条目" options={items.map((it) => ({ label: it.label, value: it.id }))} optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="featuredExhibitionIds" label="精选展览">
            <Select
              mode="multiple"
              placeholder="选择在首页展示的展览"
              options={exhibitions.map((it) => ({ label: it.label, value: it.id }))}
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="featuredResearchIds" label="精选研究">
            <Select
              mode="multiple"
              placeholder="选择在首页展示的研究"
              options={research.map((it) => ({ label: it.label, value: it.id }))}
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>
          保存首页
        </Button>
      </Card>
      {homePage === null && (
        <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
          <PictureOutlined /> 尚未创建首页数据 —— 保存后将自动创建。在此之前前台展示的是自动生成的默认首页。
        </Text>
      )}
    </div>
  )
}
